import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SocketProvider } from "./contexts/SocketContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { LoadingSpinner } from "./components/ui/loading-spinner";
import { ThemeProvider } from "./contexts/ThemeContext";
import { SidebarProvider, useSidebar } from "./contexts/SidebarContext";
import { QueryProvider } from "./contexts/QueryProvider";
import NotificationContainer from "./components/NotificationContainer";
import SupportChat from "./components/SupportChat";
import Sidebar from "./components/Sidebar";
import Header from "./components/Header";
import Dashboard from "./pages/Dashboard";
import CRM from "./pages/CRM";
import CrmConectar from "./pages/CRMConectar";
import IA from "./pages/IA";
import Agendamento from "./pages/Agendamento";
import BusinessProfile from "./pages/BusinessProfile";
import Registros from "./pages/Registros";
import Analytics from "./pages/Analytics";
import Users from "./pages/Users";
import AdminPage from "./pages/AdminPage";
import ConnectWhatsApp from "./pages/ConnectWhatsApp";
import Login from "./pages/Login";
import Chats from "./pages/Chats"; // Importar Chats
import LandingPage from "./pages/LandingPage";
import HelpPage from "./pages/HelpPage";
import useAnalytics from "./hooks/useAnalytics";

const AppContent = () => {
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const { user, loading, isAdmin } = useAuth();
  const location = useLocation();

  // Atualizar título do navegador por rota
  useEffect(() => {
    const titles = {
      "/": "PapIA - AyTech",
      "/dashboard": "PapIA - Dashboard",
      "/chats": "PapIA - Chats",
      "/crm": "PapIA - CRM",
      "/crm-conectar": "PapIA - CRM Conectar",
      "/ia": "PapIA - IA",
      "/agendamento": "PapIA - Agendamento",
      "/business-profile": "PapIA - Perfil do Negócio",
      "/contatos": "PapIA - Contatos",
      "/analytics": "PapIA - Métricas",
      "/connect-whatsapp": "PapIA - Conectar WhatsApp",
      "/users": "PapIA - Usuários",
      "/admin": "PapIA - Administração",
      "/login": "PapIA - Login",
      "/help": "PapIA - Ajuda",
    };
    document.title = titles[location.pathname] || "PapIA";
  }, [location.pathname]);

  // Ativar rastreamento do Google Analytics
  useAnalytics();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Se não estiver logado, mostrar landing page e login
  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onToggle={toggleSidebar}
        userRole={user.role}
      />

      {/* Main Content */}
      <div
        className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${sidebarOpen ? "lg:ml-64" : "lg:ml-16"
          } ml-0`}
      >
        {/* Header */}
        <Header
          user={user}
          onSidebarToggle={toggleSidebar}
          sidebarOpen={sidebarOpen}
        />

        {/* Page Content */}
        <main className={`flex-1 overflow-x-hidden bg-background ${location.pathname === '/chats'
          ? 'overflow-hidden p-0'
          : 'overflow-y-auto p-3 sm:p-6'
          }`}>
          <AnimatePresence mode="wait" initial={false}>
            <Routes location={location} key={location.pathname}>
              {/* Rotas específicas para admin */}
              {isAdmin() ? (
                <>
                  <Route path="/" element={<Navigate to="/users" replace />} />
                  <Route
                    path="/login"
                    element={<Navigate to="/users" replace />}
                  />
                  <Route
                    path="/dashboard"
                    element={<Navigate to="/users" replace />}
                  />
                  <Route
                    path="/crm"
                    element={<Navigate to="/users" replace />}
                  />
                  <Route
                    path="/ia"
                    element={<Navigate to="/users" replace />}
                  />
                  <Route
                    path="/documents"
                    element={<Navigate to="/users" replace />}
                  />
                  <Route
                    path="/contatos"
                    element={<Navigate to="/users" replace />}
                  />
                  <Route
                    path="/analytics"
                    element={<Navigate to="/users" replace />}
                  />
                  <Route
                    path="/connect-whatsapp"
                    element={<Navigate to="/users" replace />}
                  />

                  <Route path="/users" element={<Users />} />
                  <Route path="/chats" element={<Chats />} />
                  <Route path="/admin" element={<AdminPage />} />
                  <Route path="*" element={<Navigate to="/users" replace />} />
                </>
              ) : (
                <>
                  {/* Rotas para usuários normais */}
                  <Route path="/" element={<Dashboard />} />
                  <Route
                    path="/login"
                    element={<Navigate to="/dashboard" replace />}
                  />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/chats" element={<Chats />} />
                  <Route path="/crm-conectar" element={<CrmConectar />} />
                  <Route path="/crm" element={<CRM />} />
                  <Route path="/ia" element={<IA />} />
                  <Route path="/agendamento" element={<Agendamento />} />
                  <Route
                    path="/business-profile"
                    element={<BusinessProfile />}
                  />
                  <Route path="/contatos" element={<Registros />} />
                  <Route path="/analytics" element={<Analytics />} />
                  <Route
                    path="/connect-whatsapp"
                    element={<ConnectWhatsApp />}
                  />
                  <Route path="/help" element={<HelpPage />} />

                  <Route
                    path="/users"
                    element={<Navigate to="/dashboard" replace />}
                  />
                  <Route
                    path="*"
                    element={<Navigate to="/dashboard" replace />}
                  />
                </>
              )}
            </Routes>
          </AnimatePresence>
        </main>

        {/* Container de Notificações Toast */}
        <NotificationContainer />

        {/* Chatbot de Suporte */}
        <SupportChat />
      </div>
    </div>
  );
};

function App() {
  return (
    <QueryProvider>
      <ThemeProvider>
        <AuthProvider>
          <SocketProvider>
            <NotificationProvider>
              <SidebarProvider>
                <Router>
                  <AppContent />
                </Router>
              </SidebarProvider>
            </NotificationProvider>
          </SocketProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryProvider>
  );
}

export default App;
